import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Lock, Unlock, Key } from 'lucide-react'
import { useOrg } from '../context/OrgContext'
import { getUsers, lockAccount, unlockAccount, resetPassword } from '../api/endpoints'
import LoadingSpinner from '../components/LoadingSpinner'
import Button from '../components/Button'
import Input from '../components/Input'
import Modal from '../components/Modal'
import Table, { Column } from '../components/Table'
import { User } from '../api/types'
import { format } from 'date-fns'

const AccountLockout: React.FC = () => {
  const { currentOrg } = useOrg()
  const queryClient = useQueryClient()

  const [lockModalOpen, setLockModalOpen] = useState(false)
  const [unlockModalOpen, setUnlockModalOpen] = useState(false)
  const [resetModalOpen, setResetModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [lockReason, setLockReason] = useState('')
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState('')

  const { data: users, isLoading } = useQuery({
    queryKey: ['users', currentOrg?.id],
    queryFn: () => getUsers(currentOrg!.id),
    enabled: !!currentOrg,
  })

  const lockMutation = useMutation({
    mutationFn: (data: { user_id: number; reason: string }) =>
      lockAccount({ user_id: data.user_id, org_id: currentOrg!.id, reason: data.reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setLockModalOpen(false)
      setLockReason('')
      setError('')
    },
    onError: (error: Error) => {
      setError(error.message)
    },
  })

  const unlockMutation = useMutation({
    mutationFn: (userId: number) =>
      unlockAccount({ user_id: userId, org_id: currentOrg!.id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setUnlockModalOpen(false)
      setError('')
    },
    onError: (error: Error) => {
      setError(error.message)
    },
  })

  const resetMutation = useMutation({
    mutationFn: (data: { user_id: number; old_password: string; new_password: string }) =>
      resetPassword({
        user_id: data.user_id,
        org_id: currentOrg!.id,
        old_password: data.old_password,
        new_password: data.new_password,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setResetModalOpen(false)
      setOldPassword('')
      setNewPassword('')
      setError('')
    },
    onError: (error: Error) => {
      setError(error.message)
    },
  })

  const handleLock = () => {
    if (!selectedUser) return
    if (!lockReason.trim()) {
      setError('Reason is required')
      return
    }
    lockMutation.mutate({ user_id: selectedUser.id, reason: lockReason })
  }

  const handleUnlock = () => {
    if (!selectedUser) return
    unlockMutation.mutate(selectedUser.id)
  }

  const handleReset = () => {
    if (!selectedUser) return
    if (!oldPassword || !newPassword) {
      setError('Both passwords are required')
      return
    }
    resetMutation.mutate({
      user_id: selectedUser.id,
      old_password: oldPassword,
      new_password: newPassword,
    })
  }

  const columns: Column<User>[] = [
    {
      key: 'email',
      header: 'Email',
      render: (user) => <span className="font-mono text-sm">{user.email}</span>,
    },
    {
      key: 'locked',
      header: 'Status',
      render: (user) => (
        user.is_locked ? (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-risk-critical/10 text-risk-critical text-xs font-semibold">
            <Lock className="w-3 h-3" />
            Locked
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-risk-clean/10 text-risk-clean text-xs font-semibold">
            <Unlock className="w-3 h-3" />
            Active
          </span>
        )
      ),
    },
    {
      key: 'locked_at',
      header: 'Locked Since',
      render: (user) => (
        <span className="text-text-muted text-sm">
          {user.locked_at ? format(new Date(user.locked_at), 'MMM d, yyyy HH:mm') : '-'}
        </span>
      ),
    },
    {
      key: 'reset_required',
      header: 'Reset Required',
      render: (user) => (
        user.password_reset_required ? (
          <span className="text-risk-high text-sm font-semibold">Yes</span>
        ) : (
          <span className="text-text-muted text-sm">No</span>
        )
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (user) => (
        <div className="flex gap-2">
          {user.is_locked ? (
            <Button
              variant="primary"
              icon={<Unlock className="w-4 h-4" />}
              onClick={() => {
                setSelectedUser(user)
                setUnlockModalOpen(true)
              }}
            >
              Unlock
            </Button>
          ) : (
            <Button
              variant="danger"
              icon={<Lock className="w-4 h-4" />}
              onClick={() => {
                setSelectedUser(user)
                setLockModalOpen(true)
              }}
            >
              Lock
            </Button>
          )}
          <Button
            variant="secondary"
            icon={<Key className="w-4 h-4" />}
            onClick={() => {
              setSelectedUser(user)
              setResetModalOpen(true)
            }}
          >
            Reset
          </Button>
        </div>
      ),
    },
  ]

  if (!currentOrg || isLoading) {
    return <LoadingSpinner fullPage message="Loading users..." />
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Account Lockout Manager</h1>
        <p className="text-text-muted mt-1">Lock, unlock, and force password resets</p>
      </div>

      <Table
        columns={columns}
        data={users || []}
        keyExtractor={(user) => user.id}
        emptyMessage="No users found"
      />

      {/* Lock Modal */}
      <Modal isOpen={lockModalOpen} onClose={() => setLockModalOpen(false)} title="Lock Account">
        <div className="space-y-4">
          <p className="text-text-muted">
            Lock account for <span className="font-semibold text-text-primary">{selectedUser?.email}</span>
          </p>
          <Input
            label="Reason"
            placeholder="Enter reason for locking..."
            value={lockReason}
            onChange={(e) => setLockReason(e.target.value)}
            error={error}
          />
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setLockModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleLock} isLoading={lockMutation.isPending}>
              Lock Account
            </Button>
          </div>
        </div>
      </Modal>

      {/* Unlock Modal */}
      <Modal isOpen={unlockModalOpen} onClose={() => setUnlockModalOpen(false)} title="Unlock Account">
        <div className="space-y-4">
          <p className="text-text-muted">
            Unlock account for <span className="font-semibold text-text-primary">{selectedUser?.email}</span>?
          </p>
          {error && <p className="text-accent-danger text-sm">{error}</p>}
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setUnlockModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUnlock} isLoading={unlockMutation.isPending}>
              Unlock Account
            </Button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal isOpen={resetModalOpen} onClose={() => setResetModalOpen(false)} title="Force Password Reset">
        <div className="space-y-4">
          <p className="text-text-muted">
            Reset password for <span className="font-semibold text-text-primary">{selectedUser?.email}</span>
          </p>
          <Input
            type="password"
            label="Old Password"
            placeholder="Enter current password..."
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
          />
          <Input
            type="password"
            label="New Password"
            placeholder="Enter new password..."
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={error}
            helperText="Must be strong (zxcvbn score ≥3) and different from old password"
          />
          <div className="flex gap-3 justify-end">
            <Button variant="secondary" onClick={() => setResetModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleReset} isLoading={resetMutation.isPending}>
              Reset Password
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default AccountLockout
